import {
  Button,
  createStyles,
  Grid,
  Paper,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React from "react";
import SelectDropdown from "components/SelectDropdown";
import { FileUploadButton } from "features/shared/components";
import type { Retreat } from "shared/types";
import { useRainHistoryImportForm } from "../hooks/useRainHistoryImportForm";
import { useRainHistoryImportFormOptions } from "../hooks/useRainHistoryImportFormOptions";

type RainHistoryImportFormProps = {
  downloadRainHistory: (retreatId: number) => Promise<void>;
  importRainHistory: (retreatId: number, file: File) => Promise<void>;
  retreats: Retreat[];
} & WithStyles<typeof styles>;

function RainHistoryImportForm({
  classes,
  downloadRainHistory,
  importRainHistory,
  retreats,
}: RainHistoryImportFormProps) {
  const { t } = useTranslations();

  const { retreatOptions } = useRainHistoryImportFormOptions({ retreats });
  const { canSubmitForm, formState, handleResetForm, handleSelectRetreat } =
    useRainHistoryImportForm();

  const handleExportCsv = () => {
    if (formState.retreat.value == null) {
      return;
    }

    downloadRainHistory(formState.retreat.value);
  };

  const handleImportCsv = (file: File) => {
    if (formState.retreat.value == null) {
      return;
    }

    // Only accept one file
    importRainHistory(formState.retreat.value, file);
  };

  return (
    <Paper className={classes.paper} square={true}>
      <Grid item sm={12} xs={12}>
        <Typography className={classes.formHeader} gutterBottom={true}>
          {t("rain:import-or-export-rain-history")}
        </Typography>
      </Grid>

      <Grid item sm={12} xs={12}>
        <Typography className={classes.explanation} gutterBottom={true}>
          {t("rain:import-export-explanation")}
        </Typography>
      </Grid>

      <Grid item sm={12} xs={12}>
        <Typography className={classes.dropdownDisclaimer} gutterBottom={true}>
          {t("rain:import-or-export-data-from")}
        </Typography>
      </Grid>

      <SelectDropdown
        className={classes.dropdown}
        data-testid="retreat-select"
        isSearchable={true}
        label={{ text: t("locales:retreat"), required: true }}
        onChange={handleSelectRetreat}
        options={retreatOptions}
        value={formState.retreat.value}
      />

      <div className={classes.actionsContainer}>
        <Button
          aria-disabled={!canSubmitForm}
          className={classes.actionButton}
          color="primary"
          data-testid="export-csv-btn"
          disabled={!canSubmitForm}
          onClick={handleExportCsv}
          variant="contained"
        >
          {t("user:permissions.export-csv")}
        </Button>

        <FileUploadButton
          acceptedFileTypes={[".csv"]}
          className={classes.actionButton}
          data-testid="import-csv-btn"
          isDisabled={!canSubmitForm}
          multiple={false}
          onFileSelected={handleImportCsv}
        >
          {t("user:permissions.import-csv")}
        </FileUploadButton>

        <Button
          aria-disabled={!canSubmitForm}
          className={classes.actionButton}
          color="primary"
          data-testid="reset-form-btn"
          disabled={!canSubmitForm}
          onClick={handleResetForm}
          variant="contained"
        >
          {t("common:actions.cancel")}
        </Button>
      </div>
    </Paper>
  );
}

const styles = () =>
  createStyles({
    paper: {
      borderRadius: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      padding: "30px 40px",
      width: "100%",
    },
    formHeader: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
    },
    explanation: {
      color: "#535353",
      fontSize: "14px",
      fontWeight: "bold",
      letterSpacing: "-0.03px",
    },
    dropdownDisclaimer: {
      fontSize: "14px",
      fontWeight: 500,
      fontStyle: "normal",
      fontStretch: "normal",
      lineHeight: "normal",
      letterSpacing: "-0.03px",
      color: "#535353",
    },
    dropdown: {
      width: "300px !important",
    },
    actionsContainer: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: "16px",
      height: "min-content",
      marginTop: "16px",
    },
    actionButton: {
      borderRadius: "18px",
      boxShadow: "none",
      height: "100%",
      whiteSpace: "nowrap",
      width: "144px",
    },
  });

export default withStyles(styles)(RainHistoryImportForm);
